'use client';

import StarRating from '@pixum/rating';
import type { ReviewsNode } from 'data-layer';
import { scrollToElementIntoView } from 'utils';
import { REVIEW_SECTION_ID } from '../../../constants';

interface KeyVisualRatingProps {
  customerReviews?: ReviewsNode;
}

/**
 * KeyVisualRating component - Star rating display with review navigation.
 *
 * Displays customer reviews as a star rating that scrolls to the reviews section
 * when clicked. Only renders if customer review data is provided.
 *
 * @param props - KeyVisual properties containing customer reviews data
 * @returns Star rating component or null if no reviews available
 *
 * @example
 * // With customer reviews
 * <KeyVisualRating
 *   device="desktop"
 *   keyVisual={...}
 *   awards={[]}
 *   customerReviews={{
 *     rating: 4.8,
 *     ratingDescription: '1,250 verified reviews',
 *   }}
 * />
 *
 * @example
 * // Without customer reviews (renders nothing)
 * <KeyVisualRating
 *   device="mobile"
 *   keyVisual={{...}}
 *   awards={[]}
 * />
 */
export function KeyVisualRating({ customerReviews }: KeyVisualRatingProps) {
  const ratingDescription = customerReviews?.ratingDescription ? `(${customerReviews.ratingDescription})` : '';

  const handleClick = () => scrollToElementIntoView(REVIEW_SECTION_ID);

  return customerReviews ? (
    <StarRating value={customerReviews?.rating} href={`#${REVIEW_SECTION_ID}`} onClick={handleClick} description={ratingDescription}></StarRating>
  ) : undefined;
}
